import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  LayoutDashboard, BookOpen, Calendar, Users, Euro, Plus, 
  Edit, Trash2, Eye, BarChart3 
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export const AdminPage = () => {
  const { user, token, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Course Form State
  const [courseDialogOpen, setCourseDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [courseForm, setCourseForm] = useState({
    name: '',
    description: '',
    schedule: '',
    price: '',
    capacity: '',
    start_date: '',
    duration_weeks: '8',
    level: 'Anfänger'
  });

  useEffect(() => {
    if (!user || !isAdmin) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [user, isAdmin, navigate]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, coursesRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/api/admin-stats.php`, { headers }),
        axios.get(`${API}/api/courses.php?action=list`),
        axios.get(`${API}/api/bookings.php?action=all`, { headers })
      ]);
      setStats(statsRes.data);
      setCourses(coursesRes.data);
      setBookings(bookingsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Daten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const handleCourseSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const data = {
        ...courseForm,
        price: parseFloat(courseForm.price),
        capacity: parseInt(courseForm.capacity),
        duration_weeks: parseInt(courseForm.duration_weeks)
      };

      if (editingCourse) {
        await axios.put(`${API}/api/courses.php?action=update&id=${editingCourse.id}`, data, { headers });
        toast.success('Kurs aktualisiert!');
      } else {
        await axios.post(`${API}/api/courses.php?action=create`, data, { headers });
        toast.success('Kurs erstellt!');
      }

      setCourseDialogOpen(false);
      resetCourseForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!confirm('Möchten Sie diesen Kurs wirklich löschen?')) return;
    
    try {
      await axios.delete(`${API}/api/courses.php?action=delete&id=${courseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Kurs gelöscht!');
      fetchData();
    } catch (error) {
      toast.error('Kurs konnte nicht gelöscht werden');
    }
  };

  const openEditDialog = (course) => {
    setEditingCourse(course);
    setCourseForm({
      name: course.name,
      description: course.description,
      schedule: course.schedule,
      price: course.price.toString(),
      capacity: course.capacity.toString(),
      start_date: course.start_date,
      duration_weeks: course.duration_weeks.toString(),
      level: course.level
    });
    setCourseDialogOpen(true);
  };

  const resetCourseForm = () => {
    setEditingCourse(null);
    setCourseForm({
      name: '',
      description: '',
      schedule: '',
      price: '',
      capacity: '',
      start_date: '',
      duration_weeks: '8',
      level: 'Anfänger'
    });
  };

  if (!user || !isAdmin) return null;

  const tabs = [
    { id: 'overview', label: 'Übersicht', icon: LayoutDashboard },
    { id: 'courses', label: 'Kurse', icon: BookOpen },
    { id: 'bookings', label: 'Buchungen', icon: Calendar },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <main className="pt-32 pb-16 px-6" data-testid="admin-page">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8 animate-fadeIn">
            <p className="text-xs uppercase tracking-widest text-primary mb-4">
              Administration
            </p>
            <h1 className="text-4xl md:text-5xl font-heading font-bold">
              Verwaltung
            </h1>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-sm transition-all text-sm uppercase tracking-wider ${
                  activeTab === tab.id 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-secondary text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-16">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === 'overview' && stats && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 animate-fadeIn">
                  <div className="stat-card">
                    <Users className="w-8 h-8 text-primary mb-4" />
                    <div className="stat-value">{stats.total_users}</div>
                    <div className="stat-label">Benutzer</div>
                  </div>
                  <div className="stat-card">
                    <BookOpen className="w-8 h-8 text-primary mb-4" />
                    <div className="stat-value">{stats.total_courses}</div>
                    <div className="stat-label">Kurse</div>
                  </div>
                  <div className="stat-card">
                    <Calendar className="w-8 h-8 text-primary mb-4" />
                    <div className="stat-value">{stats.paid_bookings}</div>
                    <div className="stat-label">Bezahlte Buchungen</div>
                  </div>
                  <div className="stat-card">
                    <Euro className="w-8 h-8 text-primary mb-4" />
                    <div className="stat-value">€{stats.total_revenue.toFixed(2)}</div>
                    <div className="stat-label">Gesamtumsatz</div>
                  </div>
                </div>
              )}

              {/* Courses Tab */}
              {activeTab === 'courses' && (
                <div className="animate-fadeIn">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-heading font-semibold">Kursverwaltung</h2>
                    <Dialog open={courseDialogOpen} onOpenChange={(open) => {
                      setCourseDialogOpen(open);
                      if (!open) resetCourseForm();
                    }}>
                      <DialogTrigger asChild>
                        <Button className="btn-primary gap-2" data-testid="add-course-btn">
                          <Plus className="w-4 h-4" />
                          Neuer Kurs
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle className="font-heading text-xl">
                            {editingCourse ? 'Kurs bearbeiten' : 'Neuer Kurs'}
                          </DialogTitle>
                        </DialogHeader>
                        
                        <form onSubmit={handleCourseSubmit} className="space-y-4 py-4">
                          <div className="space-y-2">
                            <Label className="form-label">Kursname</Label>
                            <Input
                              value={courseForm.name}
                              onChange={(e) => setCourseForm({...courseForm, name: e.target.value})}
                              placeholder="z.B. Tango Argentino für Anfänger"
                              className="bg-secondary border-border"
                              required
                              data-testid="course-name-input"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label className="form-label">Beschreibung</Label>
                            <Textarea
                              value={courseForm.description}
                              onChange={(e) => setCourseForm({...courseForm, description: e.target.value})}
                              placeholder="Beschreiben Sie den Kurs..."
                              className="bg-secondary border-border min-h-24"
                              required
                              data-testid="course-desc-input"
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label className="form-label">Zeitplan</Label>
                              <Input
                                value={courseForm.schedule}
                                onChange={(e) => setCourseForm({...courseForm, schedule: e.target.value})}
                                placeholder="z.B. Montag 19:00 - 20:30"
                                className="bg-secondary border-border"
                                required
                                data-testid="course-schedule-input"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="form-label">Startdatum</Label>
                              <Input
                                type="date"
                                value={courseForm.start_date}
                                onChange={(e) => setCourseForm({...courseForm, start_date: e.target.value})}
                                className="bg-secondary border-border"
                                required
                                data-testid="course-date-input"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2">
                              <Label className="form-label">Preis (€)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={courseForm.price}
                                onChange={(e) => setCourseForm({...courseForm, price: e.target.value})}
                                placeholder="120.00"
                                className="bg-secondary border-border"
                                required
                                data-testid="course-price-input"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="form-label">Kapazität</Label>
                              <Input
                                type="number"
                                value={courseForm.capacity}
                                onChange={(e) => setCourseForm({...courseForm, capacity: e.target.value})}
                                placeholder="16"
                                className="bg-secondary border-border"
                                required
                                data-testid="course-capacity-input"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="form-label">Wochen</Label>
                              <Input
                                type="number"
                                value={courseForm.duration_weeks}
                                onChange={(e) => setCourseForm({...courseForm, duration_weeks: e.target.value})}
                                placeholder="8"
                                className="bg-secondary border-border"
                                required
                              />
                            </div>
                          </div>

                          <div className="space-y-2">
                            <Label className="form-label">Level</Label>
                            <Select 
                              value={courseForm.level} 
                              onValueChange={(value) => setCourseForm({...courseForm, level: value})}
                            >
                              <SelectTrigger className="bg-secondary border-border" data-testid="course-level-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Anfänger">Anfänger</SelectItem>
                                <SelectItem value="Mittelstufe">Mittelstufe</SelectItem>
                                <SelectItem value="Fortgeschritten">Fortgeschritten</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="flex gap-4 pt-4">
                            <Button type="submit" className="flex-1 btn-primary" data-testid="save-course-btn">
                              {editingCourse ? 'Aktualisieren' : 'Erstellen'}
                            </Button>
                            <Button 
                              type="button" 
                              variant="outline" 
                              onClick={() => setCourseDialogOpen(false)}
                            >
                              Abbrechen
                            </Button>
                          </div>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>

                  {/* Courses Table */}
                  <div className="bg-card border border-border rounded-md overflow-hidden">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Kurs</th>
                          <th>Level</th>
                          <th>Preis</th>
                          <th>Kapazität</th>
                          <th>Start</th>
                          <th>Aktionen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {courses.map(course => (
                          <tr key={course.id}>
                            <td>
                              <div className="font-medium">{course.name}</div>
                              <div className="text-xs text-muted-foreground">{course.schedule}</div>
                            </td>
                            <td>{course.level}</td>
                            <td className="text-accent">€{course.price.toFixed(2)}</td>
                            <td>{course.enrolled}/{course.capacity}</td>
                            <td>{new Date(course.start_date).toLocaleDateString('de-DE')}</td>
                            <td>
                              <div className="flex gap-2">
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => navigate(`/courses/${course.id}`)}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => openEditDialog(course)}
                                  data-testid={`edit-course-${course.id}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => handleDeleteCourse(course.id)}
                                  className="text-destructive hover:text-destructive"
                                  data-testid={`delete-course-${course.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Bookings Tab */}
              {activeTab === 'bookings' && (
                <div className="animate-fadeIn">
                  <h2 className="text-xl font-heading font-semibold mb-6">Alle Buchungen</h2>
                  
                  <div className="bg-card border border-border rounded-md overflow-hidden">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Benutzer</th>
                          <th>Kurs</th>
                          <th>Typ</th>
                          <th>Status</th>
                          <th>Datum</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bookings.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="text-center text-muted-foreground py-8">
                              Noch keine Buchungen vorhanden.
                            </td>
                          </tr>
                        ) : (
                          bookings.map(booking => {
                            const course = courses.find(c => c.id === booking.course_id);
                            return (
                              <tr key={booking.id}>
                                <td>
                                  <div className="font-medium">{booking.user_name}</div>
                                  <div className="text-xs text-muted-foreground">{booking.user_email}</div>
                                </td>
                                <td>{course?.name || 'Unbekannt'}</td>
                                <td>
                                  {booking.booking_type === 'couple' ? (
                                    <span className="text-primary">Paar</span>
                                  ) : (
                                    'Einzel'
                                  )}
                                </td>
                                <td>
                                  <span className={`inline-flex px-2 py-1 text-xs rounded ${
                                    booking.payment_status === 'paid' 
                                      ? 'bg-green-900/30 text-green-400' 
                                      : 'bg-yellow-900/30 text-yellow-400'
                                  }`}>
                                    {booking.payment_status === 'paid' ? 'Bezahlt' : 'Ausstehend'}
                                  </span>
                                </td>
                                <td>{new Date(booking.created_at).toLocaleDateString('de-DE')}</td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};
